import { DocumentNode, gql, useQuery } from '@apollo/client';
import { Box, Divider, HStack, Heading, Stack, StackDivider, Text, Tooltip } from '@chakra-ui/react';
import { dinero, equal, isZero } from 'dinero.js';
import { useRouter } from 'next/router';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { BigRadio } from '@/components/BigRadio';
import { BigRadioProps } from '@/components/BigRadio/BigRadio';
import { CurrencyDisplay } from '@/components/Currency';
import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { useFeatureFlags } from '@/imports/ui/hooks/useFeatureFlags';
import { getPreviousMembershipCreditsUsed } from '@/modules/membership-order/utilities';
import { isGreaterThanZero } from '@/utilities';

import { capitalize } from 'lodash';

import { RadioCard_MembershipTypeFragment } from './graphql-types/RadioCard_MembershipTypeFragment';
import { InfoIcon } from '@/imports/ui/chakra/feather';

export const RadioCard: React.FC<
  {
    membershipType: RadioCard_MembershipTypeFragment;
  } & BigRadioProps
> & {
  membershipTypeFragment: DocumentNode;
} = ({ membershipType, ...radioProps }) => {
  const { currency } = useDefaultCurrency();
  const router = useRouter();
  const isSelected = radioProps.isChecked;
  const isFutureMembership = router.query.isFutureMembership === 'true';

  const { data } = useQuery(RADIO_CARD_MEMBERSHIP_SWITCH_PREVIEW, {
    variables: {
      input: {
        membershipTypeId: membershipType._id,
        ...(isFutureMembership ? { isFutureMembership: true } : {}),
      },
    },
    skip: !isSelected,
    // The credit depends on the member's current membership, seats and
    // switches, all of which change between selections - a switch completed
    // in between left `cache-first` re-serving a preview priced against the
    // membership that had just ended. Render the cached copy instantly, then
    // replace it with a fresh one.
    fetchPolicy: 'cache-and-network',
  });


  const hasDescription = !!membershipType?.description?.blocks.find(
    (block) => !!block.text.length
  );
  const annualPrice = membershipType?.priceV2?.annual
    ? dinero(membershipType.priceV2.annual)
    : dinero({ amount: 0, currency });
  const monthlyPrice = membershipType?.priceV2?.monthly
    ? dinero(membershipType.priceV2.monthly)
    : dinero({ amount: 0, currency });

  const { isMembershipV2Enabled } = useFeatureFlags();

  const preview = data?.membershipSwitchPreview;

  const previousMembershipCreditsUsed = React.useMemo(() => {
    if (!preview) return null;

    const unusedAmount = dinero(preview.unusedAmount || { amount: 0, currency });
    const creditIssued = dinero(preview.creditIssued || { amount: 0, currency });
    const proratedPrice = preview.proratedPrice ? dinero(preview.proratedPrice) : null;

    return getPreviousMembershipCreditsUsed({
      unusedAmount,
      creditIssued,
      membershipPrice: dinero(preview.membershipPrice),
      proratedPrice,
    });
  }, [preview, currency]);

  const creditToApply = React.useMemo(() => {
    if (!preview?.creditAvailable) return null;
    return dinero(preview.creditAvailable);
  }, [preview]);

  const downgradeMessage = React.useMemo(() => {
    if (!preview || preview.switchType !== 'DOWNGRADE') return null;

    const unusedAmount = dinero(preview.unusedAmount || { amount: 0, currency });
    if (!isGreaterThanZero(unusedAmount)) return null;

    const baseAmount = preview.proratedPrice
      ? dinero(preview.proratedPrice)
      : dinero(preview.membershipPrice);

    if (isZero(baseAmount)) return null;

    const label = preview.proratedPrice ? 'prorated amount' : 'membership price';
    const amountDue = dinero(preview.amountDue || { amount: 0, currency });
    const isFullCoverage = isZero(amountDue);

    return (
      <Text textStyle="compact" color="red.600" pt="1">
        <CurrencyDisplay value={unusedAmount} /> in previous membership credits{' '}
        {isFullCoverage ? 'covers' : 'partially covers'} the {label} of{' '}
        <CurrencyDisplay value={baseAmount} />.{isFullCoverage && ' No payment is due.'}
      </Text>
    );
  }, [preview, currency]);

  return (
    <BigRadio {...radioProps}>
      <Stack w="full" direction="row" spacing="4" divider={<StackDivider />}>
        <Stack spacing="2" alignItems="flex-start" minH="140px" flex="1" minW="0" overflow="hidden">
          <Stack spacing="0">
            <Text textStyle="bodyStrong" noOfLines={1}>
              {membershipType.name}
            </Text>

            {membershipType?.__typename === 'CorporateMembershipType' && (
              <Text color="gray.600" textStyle="compact">
                {membershipType?.numberOfSeats
                  ? `Up to ${membershipType?.numberOfSeats} seat${
                      membershipType?.numberOfSeats === 1 ? '' : 's'
                    }`
                  : `Unlimited Seats`}
              </Text>
            )}
          </Stack>

          {hasDescription && membershipType?.description && (
            <AbbreviateRedraft
              rawDraftContentState={membershipType.description}
              noOfCharacters={160}
            />
          )}

          {/*display credits and unused amount on upgrade/downgrade membership from the list  */}
          {isSelected && data?.membershipSwitchPreview && (
            <Stack spacing="0">
              {data.membershipSwitchPreview.switchType && (
                <Text textStyle="compact" color="gray.600" pt="1">
                  {capitalize(data.membershipSwitchPreview.switchType)} from {data.membershipSwitchPreview.currentMembershipTypeName}
                </Text>
              )}
              {data.membershipSwitchPreview.unusedAmount && isGreaterThanZero(dinero(data.membershipSwitchPreview.unusedAmount)) && (
                <Text textStyle="compact" color="gray.600" pt="1">
                  Previous Membership Credits: <CurrencyDisplay value={dinero(data.membershipSwitchPreview.unusedAmount)} />
                </Text>
              )}
              {data.membershipSwitchPreview.switchType === 'UPGRADE' && isGreaterThanZero(dinero(data.membershipSwitchPreview.unusedAmount)) && (
                <Text textStyle="compact" color="gray.600" pt="1">
                  Previous Membership Credits are applied automatically to reduce the amount due.
                </Text>
              )}
              {!!data.membershipSwitchPreview.addonSeatsCarried && (
                <Text textStyle="compact" color="gray.600" pt="1">
                  {data.membershipSwitchPreview.addonSeatsCarried} add-on seat
                  {data.membershipSwitchPreview.addonSeatsCarried === 1 ? '' : 's'} carried
                  over. Seat price difference:{' '}
                  <CurrencyDisplay
                    value={dinero(
                      data.membershipSwitchPreview.addonSeatCharge || { amount: 0, currency }
                    )}
                  />
                </Text>
              )}
              {downgradeMessage}
              {data.membershipSwitchPreview.creditIssued && isGreaterThanZero(dinero(data.membershipSwitchPreview.creditIssued)) && (
                <Text textStyle="compact" color="red.600" pt="1">
                  After successful transaction, <CurrencyDisplay value={dinero(data.membershipSwitchPreview.creditIssued)} /> will be stored under Available credits
                </Text>
              )}
            </Stack>
          )}
        </Stack>

        <Stack
          spacing="2"
          justifyContent="space-between"
          alignItems="flex-start"
          minW={250}
          maxW={250}
        >
          <Stack spacing="0" w="full" justifyContent="center">
            {isZero(annualPrice) ? (
              <Heading size="md">Free</Heading>
            ) : isMembershipV2Enabled ? (
              <>
                <HStack justify="space-between" w="full" pt='1'>
                  <Text textStyle="compact"
                    color={membershipType ? 'gray.600' : 'gray.300'}
                    whiteSpace="nowrap"
                    fontWeight="bold">Validity</Text>
                  <Text textStyle="compact" color="gray.600" fontWeight="bold">
                    {membershipType?.validityMonths === -1
                      ? 'LifeTime'
                      : `${membershipType?.validityMonths} Month(s)`}
                  </Text>
                </HStack>
              </>
            ) : (
              <>
                <Heading size="md" as={HStack}>
                  <CurrencyDisplay value={annualPrice} />

                  <Text
                    textStyle="detailStrong"
                    color="gray.500"
                    textAlign="left"
                  >
                    /YEAR
                  </Text>
                </Heading>

                {isGreaterThanZero(monthlyPrice) && (
                  <Text textStyle="detailStrong" color="gray.500">
                    OR 12 PAYMENTS OF <CurrencyDisplay value={monthlyPrice} />
                  </Text>
                )}
              </>
            )}
            {!isZero(annualPrice) && (
              <HStack justify="space-between" w="full" pt='2'>
                <Text textStyle="compact" color="gray.600">Membership Price</Text>
                <Text textStyle="compact" color="gray.600">
                  <CurrencyDisplay value={annualPrice} />
                </Text>
              </HStack>
            )}

            {/*show amount due after credits issued when membership is selected */}
            {isSelected && data?.membershipSwitchPreview && (
              <Stack spacing="1" w="full" pt="2">
                {data.membershipSwitchPreview.proratedPrice && isGreaterThanZero(dinero(data.membershipSwitchPreview.proratedPrice)) && (
                  <HStack justify="space-between" w="full">
                    <Text textStyle="compact" color="gray.600">Prorated Price</Text>
                    <Text textStyle="compact" color="gray.600">
                      <CurrencyDisplay value={dinero(data.membershipSwitchPreview.proratedPrice)} />
                    </Text>
                  </HStack>
                )}

                {previousMembershipCreditsUsed && isGreaterThanZero(previousMembershipCreditsUsed) && (
                  <HStack justify="space-between" alignItems="baseline" w="full">
                    <Text textStyle="compact" color="success.500" minW="0" flex="1">
                      Previous Membership Credits
                      <Tooltip
                        label="Unused credit from previous membership that is automatically applied during a membership change"
                        placement="top"
                        hasArrow
                      >
                        <Box as="span" display="inline-flex" ml="1" lineHeight="1" verticalAlign="text-bottom">
                          <InfoIcon color="gray.500" display="block" />
                        </Box>
                      </Tooltip>
                    </Text>
                    <Text textStyle="compact" color="success.500" whiteSpace="nowrap">
                      - <CurrencyDisplay value={previousMembershipCreditsUsed} />
                    </Text>
                  </HStack>
                )}

                {creditToApply && isGreaterThanZero(creditToApply) && (
                  <HStack justify="space-between" w="full">
                    <Text textStyle="compact" color="gray.500">Available Credit</Text>
                    <Text textStyle="compact" color="gray.500">
                       <CurrencyDisplay value={creditToApply} />
                    </Text>
                  </HStack>
                )}


                {!!preview &&
                  (!equal(dinero(preview.amountDue), dinero(preview.membershipPrice)) ||
                    (isZero(dinero(preview.membershipPrice)) &&
                      isGreaterThanZero(
                        dinero(preview.unusedAmount || { amount: 0, currency })
                      ))) && (
                  <>
                      <Divider py="1" />
                      <HStack justify="space-between" w="full" pt='2'>
                        <Heading size="sm" whiteSpace="normal">Amount Due</Heading>
                        <Heading size="sm">
                          <CurrencyDisplay value={dinero(preview.amountDue)} />
                        </Heading>
                      </HStack>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </BigRadio>
  );
};

const RADIO_CARD_MEMBERSHIP_SWITCH_PREVIEW = gql`
  query RadioCard_MembershipSwitchPreview($input: MembershipSwitchPreviewInput!) {
    membershipSwitchPreview(input: $input) {
      currentMembershipTypeName
      newMembershipTypeName
      switchType
      membershipPrice
      proratedPrice
      unusedAmount
      creditIssued
      amountDue
      applyUnusedCredit
      creditToApply
      creditAvailable
      addonSeatsCarried
      addonSeatCharge
    }
  }
`;

RadioCard.membershipTypeFragment = gql`
  fragment RadioCard_MembershipTypeFragment on MembershipType {
    _id
    name
    priceV2 {
      annual
      monthly
      __typename
    }
    description {
      ...RawDraftContentStateFragment
      __typename
    }
    ... on CorporateMembershipType {
      numberOfSeats
      __typename
    }
    validityMonths
    allowRenewals
    position
    __typename
  }
  ${RawDraftContentStateFragmentAST}
`;
